// What this bundle was built from.
//
// Vite inlines VITE_* at build time, so these are literal strings baked into
// the JavaScript — not something read at runtime. That is the point: the value
// travels WITH the bundle, so a tab that loaded three days ago still reports
// the commit it was built from rather than the one the server is on now.
//
// 'dev' when running the dev server, 'unknown' when an image was built without
// the deploy scripts. Both are deliberately not sha-shaped so they cannot be
// mistaken for a release.
export const BUILD_SHA: string = import.meta.env.VITE_BUILD_SHA ?? 'dev';
export const BUILD_TIME: string = import.meta.env.VITE_BUILD_TIME ?? 'dev';

/** A short human label — "a1b2c3d · 22/08 08:15" — for the corner of a screen. */
export function buildLabel(): string {
  if (BUILD_SHA === 'dev' || BUILD_SHA === 'unknown') return BUILD_SHA;
  const t = new Date(BUILD_TIME);
  if (Number.isNaN(t.getTime())) return BUILD_SHA;
  const dd = String(t.getDate()).padStart(2, '0');
  const mm = String(t.getMonth() + 1).padStart(2, '0');
  const hh = String(t.getHours()).padStart(2, '0');
  const mi = String(t.getMinutes()).padStart(2, '0');
  // U+200E before the LTR run: without it the sha and the clock render in the
  // wrong order inside the RTL shell. See the bidi rule.
  return `‎${BUILD_SHA} · ${dd}/${mm} ${hh}:${mi}`;
}

/**
 * True when the server is serving a different build than this bundle came from.
 *
 * Only ever compares two real shas. If either side is 'dev'/'unknown' the
 * answer is false — a developer on the dev server should not be nagged, and an
 * unstamped image must not make every screen shout.
 */
export function isStale(
  serverSha: string | undefined | null,
  serverTime?: string | undefined | null,
): boolean {
  if (!serverSha) return false;
  const placeholder = (s: string): boolean => s === 'dev' || s === 'unknown';
  if (placeholder(serverSha) || placeholder(BUILD_SHA)) return false;
  if (serverSha === BUILD_SHA) return false;

  // Different is not the same as behind.
  //
  // This used to return true on any mismatch, so a bundle NEWER than the server
  // also counted as stale. That is not hypothetical: on 2026-08-24 the client
  // was carried onto the on-prem box ahead of its server to get a kiosk fix out,
  // and every screen on the shop floor told its operator to refresh into an
  // older build. The banner exists to catch a tab left open on yesterday's
  // JavaScript; it should stay quiet when the tab is ahead.
  //
  // Both sides carry their commit's timestamp, so ask which came first. If
  // either is unusable — 'dev', 'unknown', a clock that has not been set — fall
  // back to the old behaviour, because a mismatch nobody can order is still
  // worth mentioning.
  const server = serverTime ? Date.parse(serverTime) : NaN;
  const mine = Date.parse(BUILD_TIME);
  if (Number.isNaN(server) || Number.isNaN(mine)) return true;
  return server > mine;
}
